import React, { useState } from "react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { MessageCircle, Mail } from "lucide-react";
import EditableText from "./EditableText";
import { useEdit } from "../context/EditContext";
import { getInvolved, taxNote, contactInfo } from "../mock";
import ShareModal from "./ShareModal";

const BowlShape = () => (
  <div className="relative w-32 md:w-40 gi-bowl">
    <svg viewBox="0 0 200 120" className="w-full h-auto gi-bowl-svg" aria-hidden="true">
      <path d="M30 10 Q100 -10 170 10 Q140 95 100 95 Q60 95 30 10 Z" fill="#c89e87" />
      <rect x="85" y="88" width="30" height="14" rx="3" fill="#4a2c25" />
      <polygon points="86,102 114,102 100,118" fill="#e9dccb" />
    </svg>
    {/* Coin that drops into the bowl */}
    <svg viewBox="0 0 40 40" className="gi-coin w-6 h-6" aria-hidden="true">
      <circle cx="20" cy="20" r="18" fill="#f4c76a" stroke="#c88a2a" strokeWidth="2" />
      <circle cx="20" cy="20" r="12" fill="none" stroke="#c88a2a" strokeWidth="1.5" strokeDasharray="2 3" />
      <text x="20" y="26" textAnchor="middle" fontSize="16" fontWeight="700" fill="#7a4a10" fontFamily="Georgia, serif">{"\u20B9"}</text>
    </svg>
  </div>
);
const PersonShape = () => (
  <div className="relative w-32 md:w-40 gi-heart flex items-center justify-center">
    <svg viewBox="0 0 200 120" className="w-full h-auto gi-heart-svg" aria-hidden="true">
      <ellipse cx="100" cy="60" rx="50" ry="60" fill="#c89e87" />
      {/* Small heart inside to reinforce warmth */}
      <path d="M100 78
               C 92 68, 78 68, 78 56
               C 78 48, 86 44, 92 48
               C 96 50, 100 54, 100 54
               C 100 54, 104 50, 108 48
               C 114 44, 122 48, 122 56
               C 122 68, 108 68, 100 78 Z"
            fill="#7a2e2e" opacity="0.35" />
    </svg>
  </div>
);
const PaperShape = () => (
  <div className="relative w-32 md:w-40 gi-paper flex items-center justify-center">
    {/* Broadcast waves behind the paper */}
    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="gi-wave block w-24 h-24 rounded-full border-2 border-[#c89e87]/70" />
    </span>
    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="gi-wave gi-wave-delay block w-24 h-24 rounded-full border-2 border-[#c89e87]/70" />
    </span>
    <svg viewBox="0 0 200 120" className="relative w-full h-auto gi-paper-svg" aria-hidden="true">
      <polygon points="45,8 175,28 155,118 25,98" fill="#d6b5a0" />
      {/* Text lines on the paper */}
      <line x1="55" y1="38" x2="150" y2="52" stroke="#8a6b58" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <line x1="52" y1="58" x2="145" y2="70" stroke="#8a6b58" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      <line x1="49" y1="78" x2="130" y2="90" stroke="#8a6b58" strokeWidth="2" strokeLinecap="round" opacity="0.35" />
    </svg>
  </div>
);

const shapeFor = (s) => {
  if (s === "bowl") return <BowlShape />;
  if (s === "person") return <PersonShape />;
  return <PaperShape />;
};

const GetInvolved = () => {
  const { toast } = useToast();
  const { editMode, getValue } = useEdit();
  const [shareOpen, setShareOpen] = useState(false);

  // Editable destinations
  const donateNumber = getValue("donate.whatsapp", contactInfo.mobile);
  const volunteerEmail = getValue("volunteer.email", contactInfo.email);

  const handleAction = (label) => {
    if (label === "Donate") {
      const digits = (donateNumber || "").replace(/\D/g, "");
      if (!digits) {
        toast({ title: "WhatsApp number not set", description: "Please add a number in Edit mode." });
        return;
      }
      const text = encodeURIComponent(
        "Hi, I'd like to donate to Sharada Rama Rao Trust. Please let me know the details."
      );
      // Avoid api.whatsapp.com — blocked on some networks.
      // Mobile: use whatsapp:// deep-link (opens the app).
      // Desktop: use web.whatsapp.com (WhatsApp Web).
      const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
      const url = isMobile
        ? `whatsapp://send?phone=${digits}&text=${text}`
        : `https://web.whatsapp.com/send?phone=${digits}&text=${text}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (label === "Sign up") {
      if (!volunteerEmail) {
        toast({ title: "Email not set", description: "Please add an email in Edit mode." });
        return;
      }
      const subject = encodeURIComponent("Volunteer Sign-up \u2014 Sharada Rama Rao Trust");
      const body = encodeURIComponent(
        "Hello,\n\nI'd like to volunteer with Sharada Rama Rao Trust. Here are my details:\n\nName:\nPhone:\nCity:\nAreas of interest (e.g. Scholarships / Old-age home / Tree plantation / Eye camps):\nAvailability:\n\nThank you!"
      );
      // Use mailto: (universal) — opens the user's default email client
      // (Gmail in browser, Outlook, Apple Mail, etc.) without being blocked by Gmail URL filters.
      const url = `mailto:${volunteerEmail}?subject=${subject}&body=${body}`;
      window.location.href = url;
      return;
    }
    if (label === "Share page") {
      setShareOpen(true);
      return;
    }
    toast({ title: `${label} \u2014 coming soon`, description: "This action will be wired up once backend is added." });
  };

  return (
    <section id="get-involved" className="relative w-full bg-[#e8c9b6] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6 text-center">
        <EditableText id="gi.title" as="h2"
            className="text-[#1a1a1a] font-semibold block"
            style={{ fontFamily: "'Quicksand', system-ui, sans-serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}>
          Get involved
        </EditableText>
        <div className="mt-6 text-[#1a1a1a] space-y-1 text-base md:text-lg">
          {taxNote.map((t, i) => (
            <EditableText key={i} id={`gi.tax.${i}`} as="p">{t}</EditableText>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12">
          {getInvolved.map((card, idx) => (
            <div key={card.title} className="flex flex-col items-center">
              <div className="-mb-4 md:-mb-6 relative z-10">{shapeFor(card.shape)}</div>
              <div className="w-full bg-[#5e3422] text-[#f1e3d3] rounded-[26px] px-6 pt-10 pb-8 shadow-lg flex flex-col items-center min-h-[340px]">
                <EditableText id={`gi.card.${idx}.title`} as="h3"
                    className="text-3xl md:text-4xl font-semibold block"
                    style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
                  {card.title}
                </EditableText>
                <EditableText id={`gi.card.${idx}.desc`} as="p" multiline
                  className="mt-4 text-sm md:text-base leading-relaxed text-center max-w-[230px] block">
                  {card.description}
                </EditableText>
                <Button
                  onClick={() => handleAction(card.cta)}
                  className="mt-auto bg-[#efe6d6] hover:bg-[#e3d6bf] text-[#1a1a1a] rounded-full px-7 py-5 underline decoration-1 underline-offset-4 font-medium shadow-md"
                >
                  <EditableText id={`gi.card.${idx}.cta`} as="span">{card.cta}</EditableText>
                </Button>

                {/* Edit-mode-only helper hints so the destination is easy to change */}
                {editMode && card.cta === "Donate" && (
                  <div className="mt-4 w-full bg-black/25 rounded-lg px-3 py-2 text-[11px] text-[#f9ecd6] flex items-center justify-center gap-1.5">
                    <MessageCircle size={12} className="opacity-70" />
                    <span className="opacity-80">WhatsApp:</span>
                    <EditableText id="donate.whatsapp" as="span" className="font-semibold">
                      {contactInfo.mobile}
                    </EditableText>
                  </div>
                )}
                {editMode && card.cta === "Sign up" && (
                  <div className="mt-4 w-full bg-black/25 rounded-lg px-3 py-2 text-[11px] text-[#f9ecd6] flex items-center justify-center gap-1.5">
                    <Mail size={12} className="opacity-70" />
                    <span className="opacity-80">Email:</span>
                    <EditableText id="volunteer.email" as="span" className="font-semibold break-all">
                      {contactInfo.email}
                    </EditableText>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} />
    </section>
  );
};

export default GetInvolved;
